import { useState, useEffect, useCallback } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Spinner,
  Banner,
  BlockStack,
  InlineStack,
  Box,
  Divider,
  Icon,
  Badge,
} from "@shopify/polaris";
import {
  PlayIcon,
  TargetIcon,
  GiftCardIcon,
  AppsIcon,
  CheckCircleIcon,
  ChevronRightIcon,
} from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";
import { getCampaigns, getGames, getRewardPolicies, type Campaign } from "../api/campaigns";

interface DashboardStats {
  activeCampaigns: number;
  totalCampaigns: number;
  totalGames: number;
  totalPolicies: number;
  totalSessions: number;
}

function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    activeCampaigns: 0,
    totalCampaigns: 0,
    totalGames: 0,
    totalPolicies: 0,
    totalSessions: 0,
  });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [campaignsData, gamesData, policiesData] = await Promise.all([
        getCampaigns(),
        getGames(),
        getRewardPolicies(),
      ]);

      setCampaigns(campaignsData);

      const activeCampaigns = campaignsData.filter(c => c.status === "ACTIVE").length;
      const totalSessions = campaignsData.reduce((sum, c) => sum + (c._count?.sessions || 0), 0);

      setStats({
        activeCampaigns,
        totalCampaigns: campaignsData.length,
        totalGames: gamesData.length,
        totalPolicies: policiesData.length,
        totalSessions,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const isFirstRun = !loading && stats.totalCampaigns === 0;
  const needsSetup = !loading && (stats.totalGames === 0 || stats.totalPolicies === 0);

  if (loading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <BlockStack align="center" gap="400">
                  <Spinner size="large" />
                  <Text as="p" variant="bodyMd" tone="subdued">Loading dashboard...</Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Dashboard">
      <Layout>
        {/* Error Banner */}
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* First Run / Get Started Card */}
        {isFirstRun && (
          <Layout.Section>
            <Card>
              <Box padding="600">
                <BlockStack gap="500">
                  <BlockStack gap="200">
                    <Text as="h2" variant="headingLg" fontWeight="bold">
                      Welcome to Gamification Center
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      Engage your customers with interactive games and boost conversions with rewards. 
                      Complete the setup steps below to create your first campaign.
                    </Text>
                  </BlockStack>

                  <Divider />

                  <BlockStack gap="400">
                    <Text as="h3" variant="headingMd">Setup Checklist</Text>
                    
                    <Box
                      padding="400"
                      background={stats.totalGames > 0 ? "bg-surface-success" : "bg-surface-secondary"}
                      borderRadius="200"
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="300" blockAlign="center">
                          <Box>
                            <Icon source={stats.totalGames > 0 ? CheckCircleIcon : AppsIcon} tone={stats.totalGames > 0 ? "success" : "subdued"} />
                          </Box>
                          <BlockStack gap="100">
                            <Text as="p" variant="bodyMd" fontWeight="medium">
                              1. Browse available games
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {stats.totalGames > 0 ? `${stats.totalGames} game available` : "View the games catalog"}
                            </Text>
                          </BlockStack>
                        </InlineStack>
                        <Button
                          variant={stats.totalGames > 0 ? "tertiary" : "secondary"}
                          onClick={() => navigate("/games")}
                          icon={ChevronRightIcon}
                        >
                          {stats.totalGames > 0 ? "View" : "Browse"}
                        </Button>
                      </InlineStack>
                    </Box>

                    <Box
                      padding="400"
                      background={stats.totalPolicies > 0 ? "bg-surface-success" : "bg-surface-secondary"}
                      borderRadius="200"
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="300" blockAlign="center">
                          <Box>
                            <Icon source={stats.totalPolicies > 0 ? CheckCircleIcon : GiftCardIcon} tone={stats.totalPolicies > 0 ? "success" : "subdued"} />
                          </Box>
                          <BlockStack gap="100">
                            <Text as="p" variant="bodyMd" fontWeight="medium">
                              2. Configure reward policies
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {stats.totalPolicies > 0 ? `${stats.totalPolicies} policy configured` : "Define what customers can win"}
                            </Text>
                          </BlockStack>
                        </InlineStack>
                        <Button
                          variant={stats.totalPolicies > 0 ? "tertiary" : "secondary"}
                          onClick={() => navigate("/rewards")}
                          icon={ChevronRightIcon}
                        >
                          {stats.totalPolicies > 0 ? "View" : "Configure"}
                        </Button>
                      </InlineStack>
                    </Box>

                    <Box
                      padding="400"
                      background="bg-surface-secondary"
                      borderRadius="200"
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="300" blockAlign="center">
                          <Box>
                            <Icon source={TargetIcon} tone="subdued" />
                          </Box>
                          <BlockStack gap="100">
                            <Text as="p" variant="bodyMd" fontWeight="medium">
                              3. Create your first campaign
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              Combine a game with rewards
                            </Text>
                          </BlockStack>
                        </InlineStack>
                        <Button
                          variant="primary"
                          onClick={() => navigate("/campaigns/new")}
                          icon={ChevronRightIcon}
                        >
                          Create
                        </Button>
                      </InlineStack>
                    </Box>
                  </BlockStack>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Setup Incomplete Warning */}
        {!isFirstRun && needsSetup && (
          <Layout.Section>
            <Banner tone="warning">
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  {stats.totalGames === 0 && "No games available. "}
                  {stats.totalPolicies === 0 && "No reward policies configured."}
                </Text>
                <InlineStack gap="200">
                  {stats.totalGames === 0 && (
                    <Button size="slim" onClick={() => navigate("/games")}>View Games</Button>
                  )}
                  {stats.totalPolicies === 0 && (
                    <Button size="slim" onClick={() => navigate("/rewards")}>Create Rewards</Button>
                  )}
                </InlineStack>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        {/* Stats Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Box minWidth="200px" width="25%">
              <Card>
                <Box padding="400">
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="p" variant="bodySm" tone="subdued">Active Campaigns</Text>
                      <Icon source={TargetIcon} tone="success" />
                    </InlineStack>
                    <Text as="p" variant="heading2xl" fontWeight="bold">
                      {stats.activeCampaigns}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      of {stats.totalCampaigns} total
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
            </Box>

            <Box minWidth="200px" width="25%">
              <Card>
                <Box padding="400">
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="p" variant="bodySm" tone="subdued">Total Sessions</Text>
                      <Icon source={PlayIcon} tone="info" />
                    </InlineStack>
                    <Text as="p" variant="heading2xl" fontWeight="bold">
                      {stats.totalSessions}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      game plays
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
            </Box>

            <Box minWidth="200px" width="25%">
              <Card>
                <Box padding="400">
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="p" variant="bodySm" tone="subdued">Games Available</Text>
                      <Icon source={AppsIcon} tone="base" />
                    </InlineStack>
                    <Text as="p" variant="heading2xl" fontWeight="bold">
                      {stats.totalGames}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      in catalog
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
            </Box>

            <Box minWidth="200px" width="25%">
              <Card>
                <Box padding="400">
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="p" variant="bodySm" tone="subdued">Reward Policies</Text>
                      <Icon source={GiftCardIcon} tone="base" />
                    </InlineStack>
                    <Text as="p" variant="heading2xl" fontWeight="bold">
                      {stats.totalPolicies}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      configured
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
            </Box>
          </InlineStack>
        </Layout.Section>

        {/* Recent Campaigns */}
        {campaigns.length > 0 && (
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd" fontWeight="semibold">
                      Recent Campaigns
                    </Text>
                    <Button variant="plain" onClick={() => navigate("/campaigns")}>
                      View all
                    </Button>
                  </InlineStack>
                  <Divider />
                  <BlockStack gap="300">
                    {campaigns.slice(0, 5).map((campaign) => (
                      <InlineStack key={campaign.id} align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <Text as="p" variant="bodyMd" fontWeight="medium">
                            {campaign.name}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {campaign.placement} · {campaign._count?.sessions || 0} sessions
                          </Text>
                        </BlockStack>
                        <InlineStack gap="200" blockAlign="center">
                          <Badge
                            tone={
                              campaign.status === "ACTIVE" ? "success" :
                              campaign.status === "PAUSED" ? "warning" :
                              campaign.status === "DRAFT" ? "info" :
                              undefined
                            }
                          >
                            {campaign.status}
                          </Badge>
                          <Button size="slim" onClick={() => navigate(`/campaigns/${campaign.id}`)}>
                            Edit
                          </Button>
                        </InlineStack>
                      </InlineStack>
                    ))}
                  </BlockStack>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Quick Actions */}
        {!isFirstRun && (
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd" fontWeight="semibold">
                    Quick Actions
                  </Text>
                  <InlineStack gap="300">
                    <Button onClick={() => navigate("/campaigns/new")}>
                      New Campaign
                    </Button>
                    <Button variant="secondary" onClick={() => navigate("/rewards")}>
                      Manage Rewards
                    </Button>
                    <Button variant="secondary" onClick={() => navigate("/games")}>
                      Browse Games
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}

export default DashboardPage;
